import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  getUnsubscriberHref,
  unsubscriberColumns,
  unsubscriberRows,
} from "./tableParams/unsubscriberColumns";
import ProjectAPI from "../../../api/ProjectAPI";
import StatsAPI from "../../../api/StatsAPI";
import { navigatorLanguage } from "../../../i18n";
import { getLanguageUuid } from "../../../utils/data/Content";
import DataTable from "../../dataTable/DataTable";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ExportCsvButton from "../charts/ExportCsvButton";

import type { JSX } from "react";

import type { AnalyticsPeriod } from "@/src/hooks/usePeriodFilter";

import type { UnsubscriberRow } from "./tableParams/unsubscriberColumns.types";
import type { AppState } from "../../../reducers";

interface UnsubscribersTableProps {
  period: AnalyticsPeriod;
  userAbility: boolean;
  offerAbility: boolean;
}

function UnsubscribersTable({
  period,
  userAbility,
  offerAbility,
}: UnsubscribersTableProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { startDate, endDate } = period;

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const lang = useSelector((state: AppState) => state.variableReducer.lang);
  const projectReasons = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.unsubscribe_reasons?.reasons,
  );

  const { language_uuid: languageUuid } = getLanguageUuid(
    languages,
    navigatorLanguage,
  );

  const getPlatformsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getPlatforms,
    params: [{ projectSlug }],
  });
  const { data: platformData } = useQuery(getPlatformsQuery);

  const hasPaypalProvider = Boolean(
    platformData?.providers.some(
      (provider) => provider.payment_provider === "paypal",
    ),
  );

  const getUnsubscriberTransactionsQuery = StatsAPI.generateQuery({
    fn: StatsAPI.getUnsubscriberTransactions,
    params: [{ projectSlug, startDate, endDate, languageUuid }],
  });
  const {
    data: unsubscriberTransactions,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...getUnsubscriberTransactionsQuery,
    enabled: !!projectSlug,
  });

  const rowList = useMemo<UnsubscriberRow[]>(
    () =>
      unsubscriberRows(unsubscriberTransactions?.data ?? [], {
        t,
        lang,
        translateReasons:
          unsubscriberTransactions?.translate_reasons?.reasons ?? [],
        projectReasons,
      }),
    [unsubscriberTransactions, t, lang, projectReasons],
  );

  const columnList = useMemo(
    () =>
      unsubscriberColumns({
        t,
        projectSlug,
        userAbility,
        offerAbility,
        hasPaypalProvider,
      }),
    [t, projectSlug, userAbility, offerAbility, hasPaypalProvider],
  );

  const getRowHref = useCallback(
    (row: UnsubscriberRow): string | undefined =>
      getUnsubscriberHref(row, projectSlug, userAbility),
    [userAbility, projectSlug],
  );

  // Toute la période, côté serveur : sans la recherche ni les filtres.
  const fetchExport = async (): Promise<string> =>
    StatsAPI.getUnsubscriberTransactions({
      projectSlug,
      startDate,
      endDate,
      languageUuid,
      isExport: true,
    });

  const canExport = !isLoading && rowList.length > 0;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("unsubscribeTransactionsTitle")}</CardTitle>
        <CardAction className="flex items-center gap-2">
          <ExportCsvButton
            disabled={!canExport}
            fetchCsv={fetchExport}
            fileName="unsubscriber_transactions.csv"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "unsubscription_date", desc: true }}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading}
          onRetry={refetch}
          resetPageOn={[startDate.valueOf(), endDate.valueOf()]}
          storageKey="transactions-unsubscribers"
          urlSync={{ prefix: "unsubscribers" }}
        />
      </CardContent>
    </Card>
  );
}

export default UnsubscribersTable;
